/**
 * Monte Carlo Simulation Engine for Portfolio Analysis
 *
 * This module provides sophisticated Monte Carlo simulation capabilities for projecting
 * future portfolio performance using multivariate normal distributions with correlations.
 *
 * Key Features:
 * - Cholesky decomposition for correlated asset returns
 * - Arithmetic and geometric (lognormal) return models
 * - Seedable random number generator for reproducibility
 * - Rebalancing strategies (none, annual, quarterly)
 * - Fee and inflation adjustments
 * - Comprehensive risk metrics (VaR, CVaR, Sharpe, max drawdown)
 *
 * Educational Use: This implementation demonstrates industry-standard quantitative finance
 * techniques suitable for retirement planning and portfolio optimization applications.
 */

/**
 * Default asset class parameters
 * Expected returns and volatilities are in percentage format (e.g., 10.0 = 10%)
 */
export const DEFAULT_ASSET_PARAMS = {
  Stocks: {
    expectedReturn: 10.0,  // % per year
    volatility: 15.0,      // % standard deviation
    label: 'Stocks (Equities)'
  },
  Bonds: {
    expectedReturn: 4.5,
    volatility: 6.0,
    label: 'Bonds (Fixed Income)'
  },
  'Real Estate': {
    expectedReturn: 8.0,
    volatility: 18.0,
    label: 'Real Estate (REITs)'
  },
  Commodities: {
    expectedReturn: 5.0,
    volatility: 20.0,
    label: 'Commodities (Gold, Oil, etc.)'
  },
  Cryptocurrency: {
    expectedReturn: 40.0,
    volatility: 80.0,
    label: 'Cryptocurrency (Bitcoin, etc.)'
  },
  Cash: {
    expectedReturn: 2.5,
    volatility: 0.5,
    label: 'Cash & Equivalents'
  }
};

/**
 * Correlation matrix between asset classes
 * These correlations are based on long-term historical relationships
 * and are used to generate realistic correlated returns
 */
export const ASSET_CORRELATIONS = {
  Stocks: { Stocks: 1.0, Bonds: -0.2, 'Real Estate': 0.6, Commodities: -0.1, Cryptocurrency: 0.3, Cash: 0.0 },
  Bonds: { Stocks: -0.2, Bonds: 1.0, 'Real Estate': 0.1, Commodities: 0.0, Cryptocurrency: -0.1, Cash: 0.0 },
  'Real Estate': { Stocks: 0.6, Bonds: 0.1, 'Real Estate': 1.0, Commodities: 0.2, Cryptocurrency: 0.1, Cash: 0.0 },
  Commodities: { Stocks: -0.1, Bonds: 0.0, 'Real Estate': 0.2, Commodities: 1.0, Cryptocurrency: 0.1, Cash: 0.0 },
  Cryptocurrency: { Stocks: 0.3, Bonds: -0.1, 'Real Estate': 0.1, Commodities: 0.1, Cryptocurrency: 1.0, Cash: 0.0 },
  Cash: { Stocks: 0.0, Bonds: 0.0, 'Real Estate': 0.0, Commodities: 0.0, Cryptocurrency: 0.0, Cash: 1.0 }
};

/**
 * Seedable Random Number Generator using Linear Congruential Generator (LCG)
 * Allows for reproducible simulations when a seed is provided
 */
class SeededRandom {
  constructor(seed = Date.now()) {
    this.seed = seed % 2147483647;
    if (this.seed <= 0) this.seed += 2147483646;
  }

  next() {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }

  /**
   * Generate normal random number using Box-Muller transform
   */
  nextNormal(mean = 0, stdDev = 1) {
    const u1 = this.next();
    const u2 = this.next();
    const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
    return z0 * stdDev + mean;
  }
}

/**
 * Matrix operations for Cholesky decomposition
 */
const MatrixOps = {
  /**
   * Perform Cholesky decomposition on a positive semidefinite matrix
   * Returns lower triangular matrix L such that A = L * L^T
   */
  choleskyDecomposition(matrix) {
    const n = matrix.length;
    const L = Array(n).fill(0).map(() => Array(n).fill(0));

    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        let sum = 0;

        if (j === i) {
          // Diagonal elements
          for (let k = 0; k < j; k++) {
            sum += L[j][k] * L[j][k];
          }
          const value = matrix[j][j] - sum;
          // Handle numerical errors - ensure non-negative
          L[j][j] = Math.sqrt(Math.max(value, 1e-10));
        } else {
          // Off-diagonal elements
          for (let k = 0; k < j; k++) {
            sum += L[i][k] * L[j][k];
          }
          L[i][j] = (matrix[i][j] - sum) / L[j][j];
        }
      }
    }

    return L;
  },

  /**
   * Multiply vector by lower triangular matrix
   */
  multiplyMatrixVector(L, vector) {
    const n = L.length;
    const result = Array(n).fill(0);

    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        result[i] += L[i][j] * vector[j];
      }
    }

    return result;
  },

  /**
   * Fix correlation matrix to be positive semidefinite
   * Uses eigenvalue clipping method
   */
  nearestPositiveSemidefinite(matrix) {
    const n = matrix.length;

    // Simple approach: ensure diagonal is 1.0 and clip correlations to valid range
    const fixed = matrix.map((row, i) =>
      row.map((val, j) => {
        if (i === j) return 1.0;
        return Math.max(-0.99, Math.min(0.99, val));
      })
    );

    // Ensure symmetry
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const avg = (fixed[i][j] + fixed[j][i]) / 2;
        fixed[i][j] = avg;
        fixed[j][i] = avg;
      }
    }

    return fixed;
  }
};

/**
 * Build covariance matrix from volatilities and correlation matrix
 */
const buildCovarianceMatrix = (assets, assetParams, correlations) => {
  const n = assets.length;
  const covMatrix = Array(n).fill(0).map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      // Convert volatilities from percentage to decimal
      const vol_i = (assetParams[assets[i]]?.volatility || 0) / 100;
      const vol_j = (assetParams[assets[j]]?.volatility || 0) / 100;
      const corr = correlations[assets[i]]?.[assets[j]] ?? (i === j ? 1.0 : 0.0);

      // Covariance = corr * vol_i * vol_j
      covMatrix[i][j] = corr * vol_i * vol_j;
    }
  }

  return covMatrix;
};

/**
 * Generate correlated random returns using Cholesky decomposition
 */
const generateCorrelatedReturns = (assets, assetParams, choleskyMatrix, rng, useGeometric = false) => {
  const n = assets.length;

  // Generate independent standard normal random variables
  const independentNormals = Array(n).fill(0).map(() => rng.nextNormal(0, 1));

  // Transform to correlated normals using Cholesky matrix
  const correlatedNormals = MatrixOps.multiplyMatrixVector(choleskyMatrix, independentNormals);

  // Convert to returns for each asset
  const returns = {};
  assets.forEach((asset, i) => {
    const expectedReturn = (assetParams[asset]?.expectedReturn || 0) / 100;
    const volatility = (assetParams[asset]?.volatility || 0) / 100;

    let assetReturn;

    if (useGeometric) {
      // Geometric (lognormal) return model
      // Adjust drift for lognormal distribution: mu - 0.5 * sigma^2
      const drift = expectedReturn - 0.5 * volatility * volatility;
      const shock = volatility * correlatedNormals[i];
      assetReturn = Math.exp(drift + shock) - 1;

      // Apply realistic annual return caps to prevent extreme compounding over 30 years
      // Cap at +100% per year - even exceptional crypto years rarely exceed this in portfolio context
      assetReturn = Math.min(assetReturn, 1.0);  // Cap at +100% per year max
    } else {
      // Arithmetic (normal) return model
      const rawReturn = expectedReturn + volatility * correlatedNormals[i];

      // Bound returns to realistic range
      assetReturn = Math.max(-0.99, Math.min(rawReturn, 1.0));  // -99% to +100%
    }

    returns[asset] = assetReturn;
  });

  return returns;
};

/**
 * Normalize allocations to handle both percentage (0-100) and decimal (0-1) inputs
 */
const normalizeAllocations = (allocations) => {
  const total = Object.values(allocations).reduce((sum, val) => sum + val, 0);

  // If sum is around 1.0, assume decimal format; if around 100, assume percentage
  const isDecimalFormat = total <= 1.5;

  const normalized = {};
  Object.entries(allocations).forEach(([asset, value]) => {
    normalized[asset] = isDecimalFormat ? value : value / 100;
  });

  return normalized;
};

/**
 * Apply rebalancing to portfolio
 */
const rebalancePortfolio = (assetValues, targetAllocations) => {
  const totalValue = Object.values(assetValues).reduce((sum, val) => sum + val, 0);

  const rebalanced = {};
  Object.entries(targetAllocations).forEach(([asset, allocation]) => {
    rebalanced[asset] = totalValue * allocation;
  });

  return rebalanced;
};

/**
 * Calculate comprehensive portfolio metrics from time series
 */
const calculatePortfolioMetrics = (timeSeries, initialInvestment, numScenarios) => {
  if (!timeSeries || timeSeries.length === 0) {
    return null;
  }

  const years = timeSeries.length;
  const finalValues = timeSeries[years - 1].values;

  // Sort final values for percentile calculations
  const sortedFinalValues = [...finalValues].sort((a, b) => a - b);

  // Interpolated percentile calculation
  const getPercentile = (sortedArray, percentile) => {
    const index = (percentile / 100) * (sortedArray.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;

    if (lower === upper) {
      return sortedArray[lower];
    }
    return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
  };

  // Calculate annualized returns for each scenario
  const annualizedReturns = finalValues.map(finalValue =>
    (Math.pow(finalValue / initialInvestment, 1 / years) - 1) * 100
  );

  const meanAnnualizedReturn = annualizedReturns.reduce((sum, r) => sum + r, 0) / annualizedReturns.length;
  const medianFinalValue = getPercentile(sortedFinalValues, 50);
  const medianAnnualizedReturn = (Math.pow(medianFinalValue / initialInvestment, 1 / years) - 1) * 100;

  // Calculate volatility of annual returns across all scenarios
  const allYearReturns = [];
  for (let year = 0; year < years; year++) {
    timeSeries[year].returns.forEach(r => allYearReturns.push(r));
  }
  const meanReturn = allYearReturns.reduce((sum, r) => sum + r, 0) / allYearReturns.length;
  const variance = allYearReturns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / allYearReturns.length;
  const volatility = Math.sqrt(variance);

  // Max drawdown calculation across all scenarios
  let totalMaxDrawdown = 0;
  for (let s = 0; s < numScenarios; s++) {
    let peak = initialInvestment;
    let maxDD = 0;

    for (let year = 0; year < years; year++) {
      const value = timeSeries[year].values[s];
      if (value > peak) peak = value;
      const drawdown = ((peak - value) / peak) * 100;
      if (drawdown > maxDD) maxDD = drawdown;
    }

    totalMaxDrawdown += maxDD;
  }
  const avgMaxDrawdown = totalMaxDrawdown / numScenarios;

  // Sharpe Ratio (using 2.5% risk-free rate to match cash returns)
  const riskFreeRate = 2.5;
  const sharpeRatio = volatility > 0 ? (meanReturn - riskFreeRate) / volatility : 0;

  // Value at Risk (VaR) - 5% worst case
  const var95 = getPercentile(sortedFinalValues, 5);
  const var95Loss = initialInvestment - var95;
  const var95LossPercent = (var95Loss / initialInvestment) * 100;

  // Conditional Value at Risk (CVaR) - average of worst 5%
  const var95Index = Math.floor(sortedFinalValues.length * 0.05);
  const worstValues = sortedFinalValues.slice(0, var95Index + 1);
  const cvar95 = worstValues.reduce((sum, val) => sum + val, 0) / worstValues.length;
  const cvar95Loss = initialInvestment - cvar95;
  const cvar95LossPercent = (cvar95Loss / initialInvestment) * 100;

  // Best and worst year statistics
  // Use reduce instead of spread operator to avoid stack overflow with large arrays
  const bestYearReturn = allYearReturns.reduce((max, val) => Math.max(max, val), -Infinity);
  const worstYearReturn = allYearReturns.reduce((min, val) => Math.min(min, val), Infinity);

  // Probability of loss (ending below initial investment)
  const lossCount = sortedFinalValues.filter(v => v < initialInvestment).length;
  const probabilityOfLoss = (lossCount / sortedFinalValues.length) * 100;

  // Key percentiles
  const p10 = getPercentile(sortedFinalValues, 10);
  const p25 = getPercentile(sortedFinalValues, 25);
  const p50 = getPercentile(sortedFinalValues, 50);
  const p75 = getPercentile(sortedFinalValues, 75);
  const p90 = getPercentile(sortedFinalValues, 90);

  return {
    // Central tendency
    meanFinalValue: finalValues.reduce((sum, val) => sum + val, 0) / finalValues.length,
    medianFinalValue,
    meanAnnualizedReturn,
    medianAnnualizedReturn,

    // Risk measures
    volatility,
    avgMaxDrawdown,
    sharpeRatio,

    // Extreme outcomes
    minFinalValue: sortedFinalValues[0],
    maxFinalValue: sortedFinalValues[sortedFinalValues.length - 1],
    bestYearReturn,
    worstYearReturn,

    // Value at Risk
    var95,
    var95Loss,
    var95LossPercent,
    cvar95,
    cvar95Loss,
    cvar95LossPercent,

    // Percentiles
    p10,
    p25,
    p50,
    p75,
    p90,

    // Probabilities
    probabilityOfLoss
  };
};

/**
 * Calculate percentile bands across all scenarios for visualization
 */
const calculatePercentileBands = (timeSeries) => {
  const percentiles = {
    p5: [],
    p25: [],
    p50: [],
    p75: [],
    p95: []
  };

  const getInterpolatedPercentile = (sortedArray, percentile) => {
    const index = (percentile / 100) * (sortedArray.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;

    if (lower === upper) {
      return sortedArray[lower];
    }
    return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
  };

  timeSeries.forEach((yearData, index) => {
    const sortedValues = [...yearData.values].sort((a, b) => a - b);

    percentiles.p5.push({ year: index + 1, value: getInterpolatedPercentile(sortedValues, 5) });
    percentiles.p25.push({ year: index + 1, value: getInterpolatedPercentile(sortedValues, 25) });
    percentiles.p50.push({ year: index + 1, value: getInterpolatedPercentile(sortedValues, 50) });
    percentiles.p75.push({ year: index + 1, value: getInterpolatedPercentile(sortedValues, 75) });
    percentiles.p95.push({ year: index + 1, value: getInterpolatedPercentile(sortedValues, 95) });
  });

  return percentiles;
};

/**
 * Main Monte Carlo Simulation Function
 *
 * @param {Object} params - Simulation parameters
 * @param {Object} params.allocations - Asset allocations (percentage 0-100 or decimal 0-1)
 * @param {number} params.initialInvestment - Starting portfolio value
 * @param {number} params.yearsToSimulate - Simulation time horizon
 * @param {number} params.numScenarios - Number of Monte Carlo paths
 * @param {Object} params.assetParams - Expected returns and volatilities by asset
 * @param {Object} params.correlations - Correlation matrix between assets
 * @param {string} params.returnModel - 'arithmetic' or 'geometric' (lognormal)
 * @param {string} params.rebalancingFrequency - 'none', 'annual', or 'quarterly'
 * @param {number} params.annualFee - Annual fee as decimal (e.g., 0.01 = 1%)
 * @param {number} params.inflationRate - Annual inflation rate as percentage (e.g., 2.5)
 * @param {number} params.seed - Random seed for reproducibility (optional)
 *
 * @returns {Object} Simulation results including timeSeries, metrics, and percentiles
 */
export const calculateMonteCarlo = ({
  allocations,
  initialInvestment = 10000,
  yearsToSimulate = 30,
  numScenarios = 10000,
  assetParams = DEFAULT_ASSET_PARAMS,
  correlations = ASSET_CORRELATIONS,
  returnModel = 'geometric',  // Changed default from 'arithmetic' to 'geometric' for more realistic projections
  rebalancingFrequency = 'none',
  annualFee = 0.0,
  inflationRate = 0.0,
  seed = null
}) => {
  // Validate inputs
  if (!allocations || Object.keys(allocations).length === 0) {
    throw new Error('Portfolio allocations are required');
  }

  // Normalize allocations to decimal format
  const normalizedAllocations = normalizeAllocations(allocations);
  const totalAllocation = Object.values(normalizedAllocations).reduce((sum, val) => sum + val, 0);

  if (Math.abs(totalAllocation - 1.0) > 0.01) {
    throw new Error(`Portfolio allocations must sum to 100% or 1.0 (got ${(totalAllocation * 100).toFixed(2)}%)`);
  }

  console.log(`Running Monte Carlo: ${numScenarios} scenarios over ${yearsToSimulate} years`);
  console.log(`Return model: ${returnModel}, Rebalancing: ${rebalancingFrequency}, Fee: ${(annualFee * 100).toFixed(2)}%`);

  // Set up assets and build covariance matrix
  const assets = Object.keys(normalizedAllocations);
  const covarianceMatrix = buildCovarianceMatrix(assets, assetParams, correlations);

  // Fix correlation matrix if needed and perform Cholesky decomposition
  const fixedCovMatrix = MatrixOps.nearestPositiveSemidefinite(covarianceMatrix);
  const choleskyMatrix = MatrixOps.choleskyDecomposition(fixedCovMatrix);

  // Initialize random number generator
  const rng = seed !== null ? new SeededRandom(seed) : new SeededRandom();

  // Determine rebalancing frequency in years
  const rebalanceEveryYears = rebalancingFrequency === 'annual' ? 1 :
                               rebalancingFrequency === 'quarterly' ? 0.25 :
                               Infinity;

  // Run simulations
  const useGeometric = returnModel === 'geometric' || returnModel === 'lognormal';

  // Store time series: array of {year, values[], returns[], nominalValues[], realValues[]}
  const timeSeries = [];

  // Initialize all scenarios
  const scenarioStates = Array(numScenarios).fill(0).map(() => {
    const assetValues = {};
    assets.forEach(asset => {
      assetValues[asset] = initialInvestment * normalizedAllocations[asset];
    });
    return { assetValues };
  });

  // Track previous year values for return calculation
  let previousYearValues = Array(numScenarios).fill(initialInvestment);

  // Simulate year by year
  for (let year = 1; year <= yearsToSimulate; year++) {
    const yearValues = [];
    const yearReturns = [];
    const yearRealValues = [];

    // Generate returns for each scenario
    for (let s = 0; s < numScenarios; s++) {
      const state = scenarioStates[s];

      // Generate correlated returns for all assets
      const assetReturns = generateCorrelatedReturns(assets, assetParams, choleskyMatrix, rng, useGeometric);

      // Apply returns to each asset
      assets.forEach(asset => {
        state.assetValues[asset] *= (1 + assetReturns[asset]);
      });

      // Calculate total portfolio value
      let totalValue = Object.values(state.assetValues).reduce((sum, val) => sum + val, 0);

      // Apply rebalancing if needed
      if (rebalancingFrequency !== 'none' && year % rebalanceEveryYears === 0) {
        state.assetValues = rebalancePortfolio(state.assetValues, normalizedAllocations);
        totalValue = Object.values(state.assetValues).reduce((sum, val) => sum + val, 0);
      }

      // Apply annual fee
      if (annualFee > 0) {
        const feeAmount = totalValue * annualFee;
        totalValue -= feeAmount;

        // Reduce each asset proportionally
        const scale = (totalValue / (totalValue + feeAmount));
        assets.forEach(asset => {
          state.assetValues[asset] *= scale;
        });
      }

      // Calculate portfolio return for this year
      const previousValue = previousYearValues[s];
      const portfolioReturn = ((totalValue - previousValue) / previousValue) * 100;

      // Calculate inflation-adjusted (real) value
      const inflationAdjustment = Math.pow(1 + inflationRate / 100, year);
      const realValue = totalValue / inflationAdjustment;

      yearValues.push(totalValue);
      yearReturns.push(portfolioReturn);
      yearRealValues.push(realValue);
    }

    // Update previous year values for next iteration
    previousYearValues = [...yearValues];

    timeSeries.push({
      year,
      values: yearValues,
      returns: yearReturns,
      realValues: yearRealValues
    });
  }

  // Calculate metrics
  const metrics = calculatePortfolioMetrics(timeSeries, initialInvestment, numScenarios);

  // Calculate percentile bands for visualization
  const percentiles = calculatePercentileBands(timeSeries);

  return {
    timeSeries,
    metrics,
    percentiles,
    initialInvestment,
    yearsToSimulate,
    numScenarios,
    config: {
      returnModel,
      rebalancingFrequency,
      annualFee,
      inflationRate,
      seed
    }
  };
};

/**
 * Get a subset of scenarios for visualization
 * Returns representative sample paths to avoid rendering thousands of lines
 */
export const getSampleScenarios = (timeSeries, numScenarios, sampleSize = 100) => {
  if (numScenarios <= sampleSize) {
    // Return all scenarios
    return timeSeries.map((yearData, yearIndex) => {
      return yearData.values.map((value, scenarioIndex) => ({
        year: yearIndex + 1,
        scenario: scenarioIndex,
        value
      }));
    }).flat();
  }

  // Sample evenly distributed scenarios
  const step = Math.floor(numScenarios / sampleSize);
  const sampledIndices = [];
  for (let i = 0; i < numScenarios; i += step) {
    sampledIndices.push(i);
    if (sampledIndices.length >= sampleSize) break;
  }

  return timeSeries.map((yearData, yearIndex) => {
    return sampledIndices.map(scenarioIndex => ({
      year: yearIndex + 1,
      scenario: scenarioIndex,
      value: yearData.values[scenarioIndex]
    }));
  }).flat();
};

/**
 * Calculate Value at Risk (VaR) at given confidence level
 *
 * @param {Array} finalValues - Array of final portfolio values
 * @param {number} initialInvestment - Starting portfolio value
 * @param {number} confidenceLevel - Confidence level (e.g., 95 for 95%)
 * @returns {Object} VaR statistics
 */
export const calculateVaR = (finalValues, initialInvestment, confidenceLevel = 95) => {
  const sorted = [...finalValues].sort((a, b) => a - b);
  const percentile = 100 - confidenceLevel;

  // Interpolated percentile
  const index = (percentile / 100) * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  const varValue = lower === upper ?
    sorted[lower] :
    sorted[lower] * (1 - weight) + sorted[upper] * weight;

  const varLoss = initialInvestment - varValue;
  const varLossPercent = (varLoss / initialInvestment) * 100;

  return {
    value: varValue,
    loss: varLoss,
    lossPercent: varLossPercent,
    confidenceLevel
  };
};

/**
 * Calculate Conditional Value at Risk (CVaR) - expected loss in worst cases
 *
 * @param {Array} finalValues - Array of final portfolio values
 * @param {number} initialInvestment - Starting portfolio value
 * @param {number} confidenceLevel - Confidence level (e.g., 95 for 95%)
 * @returns {Object} CVaR statistics
 */
export const calculateCVaR = (finalValues, initialInvestment, confidenceLevel = 95) => {
  const sorted = [...finalValues].sort((a, b) => a - b);
  const percentile = 100 - confidenceLevel;
  const cutoffIndex = Math.floor(sorted.length * (percentile / 100));

  // Average of values below VaR threshold
  const worstValues = sorted.slice(0, cutoffIndex + 1);
  const cvarValue = worstValues.reduce((sum, val) => sum + val, 0) / worstValues.length;

  const cvarLoss = initialInvestment - cvarValue;
  const cvarLossPercent = (cvarLoss / initialInvestment) * 100;

  return {
    value: cvarValue,
    loss: cvarLoss,
    lossPercent: cvarLossPercent,
    confidenceLevel
  };
};

/**
 * Legacy API compatibility - maps to new calculateMonteCarlo function
 */
export const runMonteCarloSimulation = (params) => {
  const result = calculateMonteCarlo(params);

  // Transform to legacy format - map metrics to statistics with old field names
  const statistics = {
    mean: result.metrics.meanFinalValue,
    median: result.metrics.medianFinalValue,
    min: result.metrics.minFinalValue,
    max: result.metrics.maxFinalValue,
    p10: result.metrics.p10,
    p90: result.metrics.p90,
    meanReturn: ((result.metrics.meanFinalValue - result.initialInvestment) / result.initialInvestment) * 100,
    medianReturn: ((result.metrics.medianFinalValue - result.initialInvestment) / result.initialInvestment) * 100,
    probabilityOfLoss: result.metrics.probabilityOfLoss
  };

  // Calculate portfolio expected return for portfolioStats
  const normalizedAllocations = {};
  const totalAlloc = Object.values(params.allocations || {}).reduce((sum, val) => sum + val, 0);
  const isDecimal = totalAlloc <= 1.5;

  Object.entries(params.allocations || {}).forEach(([asset, value]) => {
    normalizedAllocations[asset] = isDecimal ? value : value / 100;
  });

  const assetParams = params.assetParams || DEFAULT_ASSET_PARAMS;
  const expectedReturn = Object.entries(normalizedAllocations).reduce((sum, [asset, weight]) =>
    sum + weight * (assetParams[asset]?.expectedReturn || 0), 0);

  return {
    scenarios: result.timeSeries,
    percentiles: result.percentiles,
    statistics,
    portfolioStats: {
      expectedReturn,
      volatility: result.metrics.volatility
    },
    initialInvestment: result.initialInvestment,
    yearsToSimulate: result.yearsToSimulate,
    numScenarios: result.numScenarios
  };
};

// Export all functions and constants
export default {
  calculateMonteCarlo,
  runMonteCarloSimulation,
  getSampleScenarios,
  calculateVaR,
  calculateCVaR,
  DEFAULT_ASSET_PARAMS,
  ASSET_CORRELATIONS
};

/* ============================================================================
 * EXAMPLE USAGE
 * ============================================================================

// Example 1: Basic Monte Carlo simulation
const result1 = calculateMonteCarlo({
  allocations: { Stocks: 60, Bonds: 30, Cash: 10 },
  initialInvestment: 100000,
  yearsToSimulate: 30,
  numScenarios: 10000
});

console.log('Median final value:', result1.metrics.medianFinalValue);
console.log('95% VaR:', result1.metrics.var95);

// Example 2: Advanced simulation with all features
const result2 = calculateMonteCarlo({
  allocations: { Stocks: 0.7, Bonds: 0.2, 'Real Estate': 0.1 }, // Decimal format
  initialInvestment: 500000,
  yearsToSimulate: 20,
  numScenarios: 5000,
  returnModel: 'geometric',
  rebalancingFrequency: 'annual',
  annualFee: 0.01, // 1% annual fee
  inflationRate: 2.5,
  seed: 42 // For reproducibility
});

console.log('Real (inflation-adjusted) median:', result2.timeSeries[19].realValues);
console.log('Sharpe Ratio:', result2.metrics.sharpeRatio);

// Example 3: Calculate specific risk metrics
const finalValues = result1.timeSeries[result1.timeSeries.length - 1].values;
const var99 = calculateVaR(finalValues, 100000, 99);
const cvar95 = calculateCVaR(finalValues, 100000, 95);

console.log('99% VaR loss:', var99.lossPercent + '%');
console.log('95% CVaR loss:', cvar95.lossPercent + '%');

============================================================================ */
